import { useTheme } from '@mui/material';

/**
 * Available color options with their metadata
 * This is the single source of truth for appointment type colors
 */
export interface ColorOption {
  value: string;
  hex: string;
  label: string;
}

/**
 * Hook to get all available appointment type colors
 * Returns color options with actual hex values from theme
 *
 * @returns Array of color options with value, hex, and label
 *
 * @example
 * const colors = useAvailableColors();
 * // [{ value: 'color1', hex: '#6C00CC', label: 'Purple' }, ...]
 */
export const useAvailableColors = (): ColorOption[] => {
  const theme = useTheme();

  return [
    { value: 'color1', hex: theme.palette.primary.main, label: 'Purple' },
    { value: 'color2', hex: '#D46D27', label: 'Orange' },
  ];
};

/**
 * Get human-readable label for a color enum
 *
 * @param colorEnum - Backend color enum (e.g., "color1", "color2")
 * @returns Human-readable label for the color
 *
 * @example
 * getColorLabel('color1'); // Returns "Purple"
 * getColorLabel('color2'); // Returns "Orange"
 * getColorLabel('unknown'); // Returns "Unknown"
 */
export const getColorLabel = (colorEnum: string): string => {
  const labels: Record<string, string> = {
    color1: 'Purple',
    color2: 'Orange',
  };

  return labels[colorEnum] ?? 'Unknown';
};

/**
 * Hook to map backend color enum to theme palette color
 *
 * Backend provides color identifiers ("color1", "color2", etc.) for
 * appointments and encounters. This hook maps those identifiers
 * to actual theme palette colors or specific hex values.
 *
 * Current mappings:
 * - "color1" → theme.palette.primary.main (purple)
 * - "color2" → #D46D27 (orange)
 *
 * @param colorEnum - Backend color enum (e.g., from appointment.color or encounter.color)
 * @returns Hex color string from theme palette or predefined color
 *
 * @example
 * const color = useColorFromEnum("color1"); // Returns "#6C00CC"
 * const color = useColorFromEnum("color2"); // Returns "#D46D27"
 * const color = useColorFromEnum(null); // Returns primary.main (fallback)
 */
export const useColorFromEnum = (colorEnum?: string | null): string => {
  const theme = useTheme();

  // Default fallback to primary color
  const fallback = theme.palette.primary.main;

  if (!colorEnum) {
    return fallback;
  }

  // Map backend color enum to theme palette or specific colors
  switch (colorEnum) {
    case 'color1':
      return theme.palette.primary.main;
    case 'color2':
      return '#D46D27';
    // Future colors can be added here as backend expands color options
    // case 'color3':
    //   return theme.palette.tertiary?.main ?? fallback;
    // case 'color4':
    //   return theme.palette.error.main;
    default:
      console.warn(`Unknown color enum: "${colorEnum}", using fallback`);
      return fallback;
  }
};
